import React, { useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import Lenis from "lenis";
import "lenis/dist/lenis.css";
import { gsap, ScrollTrigger, useGSAP, MOTION_OK } from "../lib/gsap";
import { scrollToId, setScroller } from "../lib/scroll";
import { Cursor, SocialRail } from "./home/Chrome";
import AvatarStage from "./home/AvatarStage";
import Landing from "./home/Landing";
import About from "./home/About";
import WhatIDo from "./home/WhatIDo";
import Career from "./home/Career";
import Work from "./home/Work";
import Stack from "./home/Stack";
import Insights from "./home/Insights";
import Contact from "./home/Contact";
import "../styles/site.css";

function Home() {
  const root = useRef(null);
  const location = useLocation();

  // Shared scroll reveals: masked headline lines slide up, [data-reveal] blocks fade in
  useGSAP(
    () => {
      const mm = gsap.matchMedia();
      mm.add(MOTION_OK, () => {
        gsap.utils.toArray(".section .mask > span").forEach((line) => {
          gsap.from(line, {
            yPercent: 110,
            duration: 1.1,
            ease: "power4.out",
            scrollTrigger: { trigger: line.parentElement, start: "top 90%" },
          });
        });

        const blocks = gsap.utils.toArray("[data-reveal]");
        gsap.set(blocks, { y: 40, autoAlpha: 0 });
        ScrollTrigger.batch(blocks, {
          start: "top 92%",
          onEnter: (batch) =>
            gsap.to(batch, { y: 0, autoAlpha: 1, duration: 0.9, ease: "power3.out", stagger: 0.08, overwrite: true }),
        });
      });

      // Fonts and images change section heights; recalculate pin and trigger positions
      const refresh = () => ScrollTrigger.refresh();
      window.addEventListener("load", refresh);
      document.fonts?.ready.then(refresh);
      return () => {
        window.removeEventListener("load", refresh);
        mm.revert();
      };
    },
    { scope: root }
  );

  /* The page loops. Its last screen is a copy of the hero, so when the scroll
     position reaches the end it can jump to the top and nothing visibly changes.
     With a mouse or trackpad, Lenis smooths the scrolling and does that wrap
     itself, in both directions. Touch screens, keyboards and reduced-motion
     visitors keep native scrolling, and the listener below wraps for them. */
  useEffect(() => {
    const smooth =
      window.matchMedia("(pointer: fine)").matches && window.matchMedia(MOTION_OK).matches
        ? new Lenis({ infinite: true })
        : null;

    const tick = (time) => smooth.raf(time * 1000);
    if (smooth) {
      smooth.on("scroll", ScrollTrigger.update);
      gsap.ticker.add(tick);
      gsap.ticker.lagSmoothing(0);
      setScroller(smooth);
    }

    const wrapAtEnd = () => {
      const end = document.documentElement.scrollHeight - window.innerHeight;
      if (end <= 0 || window.scrollY < end - 1) return;
      if (smooth && smooth.isScrolling === "smooth") return; // Lenis is already wrapping this one
      window.scrollTo(0, 0);
    };
    window.addEventListener("scroll", wrapAtEnd, { passive: true });

    return () => {
      window.removeEventListener("scroll", wrapAtEnd);
      if (!smooth) return;
      setScroller(null);
      gsap.ticker.remove(tick);
      gsap.ticker.lagSmoothing(500, 33);
      smooth.destroy();
    };
  }, []);

  // Links to /#section from other pages land here; scroll once layout is ready
  useEffect(() => {
    if (location.hash) {
      const id = location.hash.slice(1);
      requestAnimationFrame(() => scrollToId(id));
    }
  }, [location.hash]);

  return (
    <div className="site" ref={root}>
      <Cursor />
      <SocialRail />
      <AvatarStage />
      <main>
        <Landing />
        <About />
        <WhatIDo />
        <Career />
        <Stack />
        <Work />
        <Insights />
        <Contact />
      </main>
      {/* One screen of hero, repeated: the join of the scroll loop */}
      <div className="loop-end" aria-hidden="true">
        <Landing clone />
      </div>
    </div>
  );
}

export default Home;
